import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["alt", "title", "src", "preloader", "width", "height", "sources", "theme", "borderRadius", "display", "objectFit", "onLoad", "onError"];
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from 'react';
import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import { EImageSizeType } from '../../types';
import * as styles from '../../styles/image.css';
import cx from 'classnames';
import { Spinner } from '../../../loader';
import { getTheme } from '../../themes';
import { Error } from '../../internal/Error';
import { Picture } from '../../internal/Picture';
var getImageSizeType = function getImageSizeType(el) {
  var width = el.getBoundingClientRect().width;
  if (width >= 320) {
    return EImageSizeType.L;
  }
  if (width < 40) {
    return EImageSizeType.NONE;
  }
  return EImageSizeType.S;
};
var ImageComponent = /*#__PURE__*/React.forwardRef(function Image(props, ref) {
  var alt = props.alt,
    title = props.title,
    src = props.src,
    preloader = props.preloader,
    width = props.width,
    height = props.height,
    sources = props.sources,
    theme = props.theme,
    borderRadius = props.borderRadius,
    display = props.display,
    objectFit = props.objectFit,
    onLoad = props.onLoad,
    onError = props.onError,
    rest = _objectWithoutProperties(props, _excluded);
  var imageEl = React.useRef(null);
  var _React$useState = React.useState(true),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    loading = _React$useState2[0],
    setLoading = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    error = _React$useState4[0],
    setError = _React$useState4[1];
  var _React$useState5 = React.useState(EImageSizeType.NONE),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    imageSizeType = _React$useState6[0],
    setImageSizeType = _React$useState6[1];
  var fulfilled = !loading && !error;
  var themeClasses = getTheme(theme);
  var handleError = function handleError(e) {
    setLoading(false);
    setError(true);
    if (onError) {
      onError(e);
    }
  };
  var handleLoad = function handleLoad(e) {
    setLoading(false);
    if (onLoad) {
      onLoad(e);
    }
  };
  useIsomorphicLayoutEffect(function () {
    if (imageEl.current.complete) {
      setLoading(false);
      if (imageEl.current.naturalWidth === 0) {
        setError(true);
      }
    }
    setImageSizeType(getImageSizeType(imageEl.current));
  }, []);
  return /*#__PURE__*/React.createElement("div", _extends({}, rest, {
    ref: ref,
    className: cx(styles['container'], themeClasses['container']),
    style: {
      width: width,
      height: height,
      borderRadius: borderRadius,
      display: display
    }
  }), !fulfilled && /*#__PURE__*/React.createElement("div", {
    className: cx(styles['layout'], themeClasses['layout'])
  }), loading && preloader && /*#__PURE__*/React.createElement("span", {
    className: cx(styles['spinner'], themeClasses['spinner'])
  }, /*#__PURE__*/React.createElement(Spinner, {
    color: "current_color",
    size: 16
  })), error && !!imageSizeType && /*#__PURE__*/React.createElement(Error, {
    size: imageSizeType,
    className: cx(styles['error'], themeClasses['error'])
  }), /*#__PURE__*/React.createElement(Picture, {
    className: cx(styles['picture'], _defineProperty({}, styles['picture_visible'], fulfilled)),
    alt: alt,
    title: title,
    size: imageSizeType,
    objectFit: objectFit,
    onLoad: handleLoad,
    sources: sources,
    onError: handleError,
    src: src,
    ref: imageEl
  }));
});
ImageComponent.displayName = 'Image';
ImageComponent.defaultProps = {
  preloader: true,
  theme: 'light',
  borderRadius: '4px',
  sources: []
};

/**
 * Компонента изображения. <br />
 * Если в компонент предаеться список ссылкок, не забывайте последним элементом списка  <br />
 * передавать ссылку на картинку которая будет работать во всех браузерах.
 */
export var Image = /*#__PURE__*/React.memo(ImageComponent);